import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  padding: 40px;
  max-width: 600px;
  margin: 0 auto;
  font-family: Arial, sans-serif;
  border-radius: 10px;
  position: relative;
`;

const Title = styled.h2`
  text-align: left;
  color: #333;
  margin-bottom: 20px;
  font-size: 24px;
`;

const InfoRow = styled.div`
  margin-bottom: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoLabel = styled.span`
  font-weight: bold;
  color: #555;
`;

const InfoValue = styled.span`
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
  color: #333;
  background-color: #fff;
  box-sizing: border-box;
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: #45a049;
  }
`;

const Message = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 15px;
  text-align: center;
  color: ${props => (props.success ? 'green' : 'red')};
  background-color: ${props => (props.success ? '#e6ffe6' : '#ffe6e6')};
  border: 1px solid ${props => (props.success ? 'green' : 'red')};
  border-radius: 5px;
  width: 100%;
  max-width: 600px;
`;

const LinkButton = styled.a`
  display: block;
  width: 100%;
  padding: 12px;
  background-color: #007BFF;
  color: white;
  text-align: center;
  text-decoration: none;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: #0056b3;
  }
`;

const ScreenContainer = styled.div`
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Screen = styled.div`
  background: linear-gradient(to bottom, #ccc, #999);
  padding: 10px;
  margin-bottom: 20px;
  width: 100%;
  max-width: 400px;
  text-align: center;
  font-weight: bold;
  color: #444;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SeatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  width: 100%;
  max-width: 400px;
`;

const SeatBox = styled.div`
  background: ${props => (props.selected ? '#007bff' : '#e0e0e0')};
  padding: 12px;
  text-align: center;
  font-size: 14px;
  color: white;
  font-weight: bold;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.3s, transform 0.3s;
  box-shadow: ${props => (props.selected ? '0 4px 6px rgba(0, 123, 255, 0.2)' : '0 4px 6px rgba(0, 0, 0, 0.1)')};

  &:hover {
    background: ${props => (props.selected ? '#0056b3' : '#c1c1c1')};
    transform: scale(1.05);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const LinkButtonHalf = styled(LinkButton)`
  width: 48%;
  margin-right: 4%; /* 두 버튼 사이의 간격을 주기 위해 마진 추가 */
  
  &:last-child {
    margin-right: 0; /* 마지막 버튼의 오른쪽 마진을 없앰 */
  }
`;

const ProcessOrder = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState({});
  const [reservationNumber, setReservationNumber] = useState('');
  const [reservationName, setReservationName] = useState('');
  const [seatNumber, setSeatNumber] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [actualSeatNumber, setActualSeatNumber] = useState(''); // 실제 예매 좌석 정보
  const [comment, setComment] = useState('');
  const [highlightedSeats, setHighlightedSeats] = useState([]);
  const [message, setMessage] = useState(null);
  const [allSeats, setAllSeats] = useState([
    "앞 왼쪽", "앞 중앙", "앞 오른쪽", 
    "중간 왼쪽", "중간 중앙", "중간 오른쪽", 
    "뒤 왼쪽", "뒤 중앙", "뒤 오른쪽"
  ]); // 모든 좌석에 대한 목록

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await fetch(`/Acution/order-details/${orderId}`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (data) {
          setOrderDetails(data);
          setCouponCode(data.coupon_code);
          // 좌석 정보 초기화
          if (data.seat_info) {
            const seatArray = data.seat_info.split(',').map(seat => seat.trim());
            setHighlightedSeats(seatArray);  // 초기 좌석 설정
            setSeatNumber(seatArray.join(', '));  // 좌석 번호 텍스트 필드에 표시
          }
        } else {
          console.error('Order details not found.');
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
      }
    };

    const fetchUserInfo = async () => {
      try {
        const response = await fetch('/Acution/user-info', {
          credentials: 'include',
        });
        if (response.ok) {
          const userInfo = await response.json();
          setReservationName(userInfo.user_name || '');
          setComment('');
          
        } else {
          console.error('Failed to fetch user info.');
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    fetchOrderDetails();
    fetchUserInfo();
  }, [orderId]);

  const handleSeatClick = (seat) => {
    let updatedSeats = highlightedSeats.includes(seat)
      ? highlightedSeats.filter(s => s !== seat)
      : [...highlightedSeats, seat];
    setHighlightedSeats(updatedSeats);
    setSeatNumber(updatedSeats.join(', '));
  };

  const handleManualSeatChange = (e) => {
    setSeatNumber(e.target.value);
    setHighlightedSeats(e.target.value.split(',').map(seat => seat.trim()));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/Acution/process-order', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          movie_title : orderDetails.movie_title,
          theater:orderDetails.theater,
          menu_id: orderId,
          reservation_number: reservationNumber,
          reservation_name: reservationName,
          seat_number: seatNumber,
          actual_seat_number: actualSeatNumber, // 실제 예매된 좌석 정보 추가
          comment: comment,
          coupon_code : couponCode,
          complete_code:complete_code,
        }),
      });

      if (response.ok) {
        alert('주문이 성공적으로 처리되었습니다.');
        setMessage({ type: 'success', text: '주문이 성공적으로 처리되었습니다.' });

          // 3초 후 리다이렉트
      setTimeout(() => {
        navigate('/Tiketting/AssignOrder');
      }, 3000);
    } else {
      const errorData = await response.json(); // 서버에서 반환된 에러 메시지를 가져옴

      // 서버에서 비용 부족 오류 발생 시 처리
      console.log(JSON.stringify(errorData));
      if (errorData.error === 'Insufficient points balance to process the order.') {
        alert('포인트 잔액이 부족합니다. 충전 후 다시 시도해주세요.');
        setMessage({ type: 'error', text: '포인트 잔액이 부족합니다.' });
      } else {
        setMessage({ type: 'error', text: `주문 처리에 실패했습니다. 서버 응답: ${errorData.error || '입력 정보를 확인해주세요.'}` });
      }
    }
  } catch (error) {
    setMessage({ type: 'error', text: '주문 처리 중 오류가 발생했습니다.' });
    console.error('Error processing the order:', error);
  }
};

  return (
    <Container>
      {message && (
        <Message success={message.type === 'success'}>
          {message.text}
        </Message>
      )}
      <Title>주문 처리</Title>
      <InfoRow>
        <InfoLabel>쿠폰 번호:</InfoLabel>
        <InfoValue>{orderDetails.coupon_code}</InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel>영화 제목:</InfoLabel>
        <InfoValue>{orderDetails.movie_title}</InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel>상영관:</InfoLabel>
        <InfoValue>{orderDetails.theater}</InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel>상영일자:</InfoLabel>
        <InfoValue>{orderDetails.date}</InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel>상영 시간:</InfoLabel>
        <InfoValue>{orderDetails.screening_time}</InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel>좌석 정보:</InfoLabel>
        <InfoValue>{seatNumber}</InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel>티켓 수량:</InfoLabel>
        <InfoValue>{orderDetails.viewer_count}</InfoValue>
      </InfoRow>
      {/* <ScreenContainer>
        <Screen>스크린</Screen>
        <SeatsGrid>
          {allSeats.map((seat, index) => (
            <SeatBox
              key={index}
              selected={highlightedSeats.includes(seat)}
              onClick={() => handleSeatClick(seat)}
            >
              {seat}
            </SeatBox>
          ))}
        </SeatsGrid>
      </ScreenContainer> */}
      <ButtonRow>
        <LinkButtonHalf href={orderDetails.booking_url_pc} target="_blank" rel="noopener noreferrer">
          PC 예매 링크
        </LinkButtonHalf>
        <LinkButtonHalf href={orderDetails.booking_url_mobile} target="_blank" rel="noopener noreferrer">
          모바일 예매 링크
        </LinkButtonHalf>
      </ButtonRow>
      <form onSubmit={handleSubmit}>
      <Input
        type="text"
        placeholder="실제 예매 좌석 번호 입력"
        value={actualSeatNumber}
        onChange={(e) => setActualSeatNumber(e.target.value)}
      />
        <Input
          type="text"
          placeholder="예약 번호"
          value={reservationNumber}
          onChange={(e) => setReservationNumber(e.target.value)}
          required
        />
        <Input
          type="text"
          placeholder="예약자 이름"
          value={reservationName}
          readOnly
        />
        <Input
          type="text"
          placeholder="전달사항"
          value={comment}
          onChange={(e)=>setComment(e.target.value)}
        />
        <Button type="submit">주문 처리 완료</Button>
      </form>
    </Container>
  );
};

export default ProcessOrder;
